<template>
  <aside class="project-tree-menu">
    <div class="tree-header">
      <div class="search-box">
        <b-input :value="searchString" @input="updateSearchString" placeholder="Search folders..." type="search"
          icon="search" size="is-small" />
      </div>
      <div class="tree-filter">
        <div class="field has-addons">
          <p class="control">
            <button class="button is-small" :class="{ 'is-primary': all }" @click="setAllFilter(true)"
              title="Show all folders">
              All
            </button>
          </p>
          <p class="control">
            <button class="button is-small" :class="{ 'is-primary': !all }" @click="setAllFilter(false)"
              title="Show my folders">
              My
            </button>
          </p>
          <p class="control ml-2">
            <button class="button is-small" @click="toggleSortOrder" :title="sortTitle">
              <span class="icon is-small">
                <i :class="sortIconClass"></i>
              </span>
            </button>
          </p>
        </div>
      </div>
    </div>
    <ul class="menu-list">
      <li v-for="project in filteredProjects" :key="project.id" class="project-item">
        <a @click="toggleProject(project)" @contextmenu.prevent="showContextMenu($event, 'project', project)"
          :class="{ 'is-active': selectedItemType === 'project' && selectedItem && selectedItem.id === project.id }"
          class="project-link">
          <span class="icon is-small expand-icon">
            <i :class="project.isExpanded ? 'fas fa-angle-down' : 'fas fa-angle-right'"></i>
          </span>
          <span class="icon is-small folder-icon">
            <i class="fas fa-folder"></i>
          </span>
          <span class="project-name">{{ project.name }}</span>
          <b-tooltip :label="getAiStatusHint(aiStatuses[project.id])" position="is-top" type="is-dark"
            v-if="aiStatuses[project.id]" class="ai-status-tooltip">
            <div class="ai-status" style="display:flex; align-items:center; gap: 4px; cursor: help;">
              <span class="icon is-small has-text-grey-light">
                <i class="fas fa-robot"></i>
              </span>
              <span class="icon is-small has-text-warning" v-if="aiStatuses[project.id] === 'Process'">
                <i class="fas fa-spinner fa-spin"></i>
              </span>
              <span class="icon is-small has-text-danger" v-else-if="aiStatuses[project.id] === 'Error'">
                <i class="fas fa-exclamation-circle"></i>
              </span>
              <span class="icon is-small has-text-success" v-if="aiStatuses[project.id] === 'Ready'">
                <i class="fas fa-check-circle"></i>
              </span>
            </div>
          </b-tooltip>
        </a>
        <ul v-if="project.isExpanded" class="image-group-list">
          <li v-for="imageGroup in getFilteredImageGroups(project.imageGroups)" :key="imageGroup.id"
            class="image-group-item">
            <a @click="selectImageGroup(imageGroup)"
              @contextmenu.prevent="showContextMenu($event, 'imageGroup', imageGroup)"
              :class="{ 'is-active': selectedItemType === 'imageGroup' && selectedItem && selectedItem.id === imageGroup.id }"
              class="image-group-link">
              <span class="icon is-small folder-icon">
                <i class="fas fa-folder"></i>
              </span>
              <span class="image-group-name">{{ imageGroup.name }}</span>
            </a>
          </li>
        </ul>
      </li>
    </ul>
    <div v-if="filteredProjects.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-folder-open"></i>
      </div>
      <p class="empty-text">
        {{ searchString ? 'No folders match your search' : 'No projects found' }}
      </p>
    </div>

    <!-- Pagination -->
    <div class="pagination-wrapper mt-4 mb-2 pr-4 pl-4" v-if="totalProjects > projectPerPage">
      <b-pagination :total="totalProjects" :current.sync="internalProjectCurrentPage" :per-page="projectPerPage"
        @change="onPageChange" order="is-centered" size="is-small" icon-prev="chevron-left" icon-next="chevron-right" />
    </div>
    <ContextMenu ref="contextMenu" :items="contextMenuItems" @item-click="handleMenuItemClick"
      v-click-outside="closeContextMenu" />
  </aside>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ContextMenu from './ContextMenu.vue';
import Cytomine from '@/api/cytomine';

export default {
  name: 'ProjectTree',
  components: {
    ContextMenu
  },
  data() {
    return {
      contextMenuItem: null,
      contextMenuType: null,
      contextMenuItems: [],
      aiStatuses: {}
    };
  },
  props: {
    selectedItem: {
      type: Object,
      default: null
    },
    selectedItemType: {
      type: String,
      default: null
    }
  },
  created() {
    this.searchDebounceTimer = null;
  },
  computed: {
    ...mapState('project-tree', ['projects', 'all', 'searchString', 'sortOrder']),
    ...mapGetters('project-tree', ['filteredProjects', 'getFilteredImageGroups', 'projectCurrentPage', 'projectPerPage', 'totalProjects']),

    internalProjectCurrentPage: {
      get() {
        return this.projectCurrentPage;
      },
      set(value) {
        // Vue expects setter for .sync binding
      }
    },

    sortIconClass() {
      if (this.sortOrder === 'asc') return 'fas fa-sort-alpha-down';
      if (this.sortOrder === 'desc') return 'fas fa-sort-alpha-up';
      return 'fas fa-sort';
    },

    sortTitle() {
      if (this.sortOrder === 'asc') return 'Sort: A-Z';
      if (this.sortOrder === 'desc') return 'Sort: Z-A';
      return 'Sort: Default';
    }
  },
  watch: {
    projects: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0) {
          this.fetchAllJobsStatus();
        }
      }
    }
  },
  methods: {
    ...mapActions('project-tree', ['toggleProject', 'selectImageGroup', 'setAllFilter', 'setSortOrder']),

    toggleSortOrder() {
      let newOrder;
      if (this.sortOrder === null) {
        newOrder = 'asc';
      } else if (this.sortOrder === 'asc') {
        newOrder = 'desc';
      } else {
        newOrder = null;
      }
      this.setSortOrder(newOrder);
    },

    updateSearchString(value) {
      this.$store.commit('project-tree/SET_SEARCH_STRING', value);

      // Debounce the API call
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.$store.dispatch('project-tree/setProjectPage', 1);
      }, 500);
    },

    onPageChange(page) {
      if (page !== this.projectCurrentPage) {
        this.$store.dispatch('project-tree/setProjectPage', page);
      }
    },

    getAiStatusHint(status) {
      if (!status) return '';
      if (status === 'Process') return 'AI is currently analyzing this folder.';
      if (status === 'Error') return 'An error occurred during AI analysis in this folder.';
      if (status === 'Ready') return 'AI analysis completed for this folder.';
      return `AI Status: ${status}`;
    },

    async fetchAllJobsStatus() {
      const jobsPromises = this.projects.map(project =>
        Cytomine.instance.api.get(`ai-algorithm-jobs?projectId=${project.id}`)
          .then(response => {
            let jobs = response.data;
            if (response.data.collection) jobs = response.data.collection;
            else if (!Array.isArray(jobs)) jobs = [jobs];

            if (jobs && jobs.length > 0) {
              jobs.sort((a, b) => (a.created || 0) - (b.created || 0));
              let latestJob = jobs[jobs.length - 1];
              let status = latestJob.status;

              let finalStatus = 'Ready';
              if (['Processing', 'Running', 'Starting'].includes(status)) finalStatus = 'Process';
              else if (['Error', 'Failed'].includes(status)) finalStatus = 'Error';

              this.$set(this.aiStatuses, project.id, finalStatus);
            }
          })
          .catch(err => {
            console.error("Failed to fetch jobs for project ", project.id, err);
          })
      );
      await Promise.all(jobsPromises);
    },

    showContextMenu(event, type, item) {
      this.contextMenuItem = item;
      this.contextMenuType = type;
      if (type === 'project') {
        this.contextMenuItems = [
          {
            label: 'Add Sub-folder',
            action: 'add-subfolder',
            icon: 'fas fa-folder-plus'
          },
          {
            label: 'Rename',
            action: 'rename',
            icon: 'fas fa-edit'
          }
        ];
      } else if (type === 'imageGroup') {
        this.contextMenuItems = [
          {
            label: 'Rename',
            action: 'rename',
            icon: 'fas fa-edit'
          },
          {
            label: 'Delete',
            action: 'delete',
            icon: 'fas fa-trash'
          }
        ];
      }
      this.$refs.contextMenu.open(event);
    },
    closeContextMenu() {
      if (this.$refs.contextMenu) {
        this.$refs.contextMenu.close();
      }
    },
    handleMenuItemClick(item) {
      this.closeContextMenu();
      if (item.action === 'add-subfolder') {
        this.$emit('add-subfolder', this.contextMenuItem);
      }
      else if (item.action === 'rename') {
        this.$emit('rename-item', { type: this.contextMenuType, item: this.contextMenuItem });
      }
      else if (item.action === 'delete') {
        this.$emit('delete-item', { type: this.contextMenuType, item: this.contextMenuItem });
      }
    }
  },

  async created() {
    await this.fetchAllJobsStatus();
  },

  mounted() {
    this.$eventBus.$on('shortkeyEvent', this.shortkeyHandler);
  },
  beforeDestroy() {
    this.$store.dispatch('project-tree/setActiveImageGroupForViewer', null);
    this.$eventBus.$off('shortkeyEvent', this.shortkeyHandler);
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/dark-variables.scss';

.project-tree-menu {
  height: 100%;
  display: flex;
  flex-direction: column;

  .tree-header {
    flex-shrink: 0;
    padding: 0 0 1rem 0;
    border-bottom: 1px solid $dark-border-color;
    margin-bottom: 1rem;

    .search-box {
      margin-bottom: 0.75rem;

      ::v-deep .control {
        width: 100%;
      }

      ::v-deep .input {
        background-color: $dark-bg-hover;
        border-color: $dark-border-color;
        color: $dark-text-primary;

        &:focus {
          border-color: $primary;
          box-shadow: 0 0 0 0.125em rgba($primary, 0.25);
        }

        &::placeholder {
          color: $dark-text-disabled;
        }
      }

      ::v-deep .icon {
        color: $dark-text-disabled;
      }
    }

    .tree-filter {
      display: flex;
      justify-content: flex-end;

      .button {
        border-radius: 4px;
        padding: 0.25rem 0.75rem;
        font-size: 0.85em;
        margin-left: 0.25rem;

        &.is-primary {
          background-color: $primary;
          border-color: $primary;
          color: $dark-bg-primary;
        }
      }
    }
  }

  .menu-list {
    margin: 0;
    padding: 0;
    flex-grow: 1;
    overflow-x: hidden;
    overflow-y: auto;

    .project-item {
      margin-bottom: 0.5rem;

      .project-link {
        display: flex;
        align-items: center;
        padding: 0.75rem 1rem;
        border-radius: 6px;
        transition: all 0.2s ease;
        color: $dark-text-primary;
        text-decoration: none;
        font-weight: 500;
        position: relative;

        &:hover {
          background-color: $dark-bg-hover;
          transform: translateX(4px);
        }

        &.is-active {
          background-color: rgba($primary, 0.2);
          border-left: 3px solid $primary;
          color: $primary;

          .folder-icon i {
            color: $primary;
          }
        }

        .expand-icon {
          margin-right: 8px;
          opacity: 0.7;
          transition: transform 0.2s ease;
        }

        .folder-icon {
          margin-right: 10px;
          color: $warning;
        }

        .project-name {
          flex-grow: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .ai-status-tooltip {
          margin-left: 10px;
          flex-shrink: 0;
        }
      }

      .image-group-list {
        margin: 0.5rem 0 0 1.5rem;
        padding: 0;
        list-style: none;

        .image-group-item {
          margin-bottom: 0.25rem;

          .image-group-link {
            display: flex;
            align-items: center;
            padding: 0.5rem 0.75rem;
            border-radius: 4px;
            transition: all 0.2s ease;
            color: $dark-text-secondary;
            text-decoration: none;
            font-size: 0.95em;

            &:hover {
              background-color: $dark-bg-hover;
              color: $dark-text-primary;
            }

            &.is-active {
              background-color: rgba($info, 0.2);
              color: $info;

              .folder-icon i {
                color: $info;
              }
            }

            .folder-icon {
              margin-right: 8px;
              color: $warning;
            }

            .image-group-name {
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        }
      }
    }
  }

  .empty-state {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem;
    color: $dark-text-disabled;

    .empty-icon {
      font-size: 2.5rem;
      margin-bottom: 1rem;
      opacity: 0.6;
    }

    .empty-text {
      font-size: 0.9rem;
      opacity: 0.8;
    }
  }

  .pagination-wrapper {
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 1rem;
    border-top: 1px solid $dark-border-color;
  }
}
</style>